import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { optimizationService } from '../services/optimization.service';

const OptimizationRunDetailsDialog = ({ 
  open, 
  onClose, 
  runId
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOptRun, setSelectedOptRun] = useState(null);

  // Fetch optimization run details when dialog opens or runId changes
  useEffect(() => {
    if (open && runId) {
      fetchOptimizationRunDetails();
    }
  }, [open, runId]);

  const fetchOptimizationRunDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Fetching optimization run details for ID: ${runId}`);
      const response = await optimizationService.getOptimizationRunById(runId);
      console.log('API Response:', response);
      
      // The API returns the data directly in the response object
      if (response && response.run_metadata) {
        // This is the format we're seeing in the console
        setSelectedOptRun(response);
      } else if (response && response.data && response.data.run_metadata) {
        // Handle case where response has a data property
        setSelectedOptRun(response.data);
      } else if (response && response.status === 'success' && response.data) {
        // Handle case where response has a status and data property
        setSelectedOptRun(response.data);
      } else {
        setError('Invalid response format from API');
        console.error('Invalid API response format:', response);
      }
    } catch (error) {
      console.error('Error fetching optimization run details:', error);
      setError('An error occurred while fetching optimization run details');
    } finally {
      setLoading(false);
    }
  };

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Optimization Run Details
        {loading && <CircularProgress size={24} sx={{ ml: 2 }} />}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : selectedOptRun ? (
          <Box>
            {/* Metadata */}
            <Typography variant="h6" gutterBottom>Run Information</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Run ID:</Typography>
                  <Typography variant="body2">{selectedOptRun.run_metadata?.inventory_run_id || runId}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Status:</Typography>
                  <Chip 
                    label={selectedOptRun.run_metadata?.status || 'UNKNOWN'} 
                    color={selectedOptRun.run_metadata?.status === 'COMPLETED' ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Created At:</Typography>
                  <Typography variant="body2">{formatDate(selectedOptRun.run_metadata?.created_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Completed At:</Typography>
                  <Typography variant="body2">{formatDate(selectedOptRun.run_metadata?.completed_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Created By:</Typography>
                  <Typography variant="body2">{selectedOptRun.run_metadata?.created_by || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Processing Time:</Typography>
                  <Typography variant="body2">
                    {selectedOptRun.run_metadata?.processing_time_seconds 
                      ? `${selectedOptRun.run_metadata.processing_time_seconds} seconds` 
                      : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            
            {/* Inventory Parameters */}
            {selectedOptRun.configuration?.inventory_parameters && (
              <>
                <Typography variant="h6" gutterBottom>Inventory Parameters</Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  {selectedOptRun.configuration.inventory_parameters?.global_parameters ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Version:</Typography>
                        <Typography variant="body2">
                          {selectedOptRun.configuration.inventory_parameters.global_parameters.version || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Run Time:</Typography>
                        <Typography variant="body2">
                          {selectedOptRun.configuration.inventory_parameters.global_parameters.run_time || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Timezone:</Typography>
                        <Typography variant="body2">
                          {selectedOptRun.configuration.inventory_parameters.global_parameters.timezone || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Batch Size:</Typography>
                        <Typography variant="body2">
                          {selectedOptRun.configuration.inventory_parameters.global_parameters.batch_size || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Last Updated:</Typography>
                        <Typography variant="body2">
                          {selectedOptRun.configuration.inventory_parameters.global_parameters.last_updated || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Default Service Level:</Typography>
                        <Typography variant="body2">
                          {selectedOptRun.configuration.inventory_parameters.global_parameters.default_service_level 
                            ? `${(selectedOptRun.configuration.inventory_parameters.global_parameters.default_service_level * 100).toFixed(0)}%` 
                            : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Optimization Frequency:</Typography>
                        <Typography variant="body2">
                          {selectedOptRun.configuration.inventory_parameters.global_parameters.optimization_frequency || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Default Lead Time Buffer:</Typography>
                        <Typography variant="body2">
                          {selectedOptRun.configuration.inventory_parameters.global_parameters.default_lead_time_buffer || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Default Safety Stock Days:</Typography>
                        <Typography variant="body2">
                          {selectedOptRun.configuration.inventory_parameters.global_parameters.default_safety_stock_days || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">Inventory Optimization Algorithm:</Typography>
                        <Typography variant="body2">
                          {selectedOptRun.configuration.inventory_parameters.global_parameters.inventory_optimization?.algorithm || 'N/A'}
                        </Typography>
                      </Grid>
                      {selectedOptRun.configuration.inventory_parameters.global_parameters.inventory_optimization?.parameters?.days_of_coverage && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2">Target Days of Coverage:</Typography>
                            <Typography variant="body2">
                              {selectedOptRun.configuration.inventory_parameters.global_parameters.inventory_optimization.parameters.days_of_coverage.target_days || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2">Lookback Days:</Typography>
                            <Typography variant="body2">
                              {selectedOptRun.configuration.inventory_parameters.global_parameters.inventory_optimization.parameters.days_of_coverage.lookback_days || 'N/A'}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  ) : (
                    <Typography>No inventory parameters available</Typography>
                  )}
                </Paper>
              </>
            )}
            
            {/* Configuration Summary */}
            {selectedOptRun.configuration && (
              <>
                <Typography variant="h6" gutterBottom>Configuration</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">Forecast Run ID:</Typography>
                      <Typography variant="body2">{selectedOptRun.configuration.forecast_run_id || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">Sourcing Algorithm:</Typography>
                      <Typography variant="body2">
                        {selectedOptRun.configuration.sourcing_algorithm || 
                         selectedOptRun.configuration.sourcing_config?.global_defaults?.sourcing_algorithm || 
                         'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">Default Lead Time (days):</Typography>
                      <Typography variant="body2">
                        {selectedOptRun.configuration.default_lead_time_days || 
                         selectedOptRun.configuration.sourcing_config?.po_generation_config?.default_lead_time_days || 
                         'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">PO Grouping Strategy:</Typography>
                      <Typography variant="body2">
                        {selectedOptRun.configuration.po_grouping_strategy || 
                         selectedOptRun.configuration.sourcing_config?.po_generation_config?.grouping_strategy || 
                         'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </>
            )}
          </Box>
        ) : (
          <Alert severity="info">No optimization run details available for ID: {runId}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default OptimizationRunDetailsDialog;